import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Lifebuoy, Buildings, MagnifyingGlass, Eye, EnvelopeSimple, LockKeyOpen,
  Power, UserGear, PlusCircle, PencilSimple, CalendarPlus,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const fieldClass =
  "w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary";

// Generic support action dialog (no reason required).
function FormDialog({ spec, onClose }) {
  const [values, setValues] = useState(() => {
    const init = { reason: "" };
    (spec.fields || []).forEach((f) => (init[f.name] = f.default ?? ""));
    return init;
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    try {
      await spec.action(values);
      toast.success(spec.successMsg || "Done");
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="support-dialog">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-2xl">
        <h3 className="mb-3 text-base font-bold text-foreground">{spec.title}</h3>
        <div className="space-y-3">
          {(spec.fields || []).map((f) => (
            <div key={f.name}>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">{f.label}</label>
              {f.type === "select" ? (
                <select data-testid={`support-field-${f.name}`} className={fieldClass}
                  value={values[f.name]} onChange={set(f.name)}>
                  <option value="">Select…</option>
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <Input data-testid={`support-field-${f.name}`} value={values[f.name]}
                  onChange={set(f.name)} placeholder={f.placeholder || ""} />
              )}
              {f.hint && <p className="mt-1 text-[11.5px] text-muted-foreground">{f.hint}</p>}
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onClose(false)} data-testid="support-dialog-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="support-dialog-submit">
            {busy ? "Working…" : (spec.submitLabel || "Confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Simple, self-contained "Add a District Admin" card: pick district + year, enter email, invite.
function AddAdminCard({ districts, onDone }) {
  const [districtId, setDistrictId] = useState("");
  const [ry, setRy] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const d = districts.find((x) => x.district_id === districtId);
  const years = d?.rotary_years || [];
  useEffect(() => {
    setRy(years.find((y) => y.status === "active")?.rotary_year_id || years[0]?.rotary_year_id || "");
  }, [districtId]); // eslint-disable-line
  const submit = async () => {
    if (!districtId) return toast.error("Choose a district");
    if (!ry) return toast.error("Choose a Rotary Year");
    if (!email.includes("@")) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      await api.post(`/support/districts/${districtId}/admin`, {
        email: email.trim(), display_name: name.trim(), rotary_year_id: ry,
        send_invite: true, reason: "Add district admin" });
      toast.success("Admin invited — they'll get an email to set up their login");
      setEmail(""); setName(""); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add admin"); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4" data-testid="add-admin-card">
      <div className="flex items-center gap-2">
        <UserGear size={18} weight="bold" className="text-primary" />
        <h3 className="text-base font-bold text-foreground">Add a District Admin</h3>
      </div>
      <p className="mt-0.5 mb-3 text-xs text-muted-foreground">Pick a district and its Rotary Year, enter the person's email — they get an invite to set up their own login. That's it.</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
        <div className="lg:col-span-1">
          <label className="mb-1 block text-xs font-semibold text-muted-foreground">District</label>
          <select data-testid="add-admin-district" className={fieldClass} value={districtId} onChange={(e) => setDistrictId(e.target.value)}>
            <option value="">Select…</option>
            {districts.map((x) => <option key={x.district_id} value={x.district_id}>District {x.rotary_district_no} — {x.name}</option>)}
          </select>
        </div>
        <div className="lg:col-span-1">
          <label className="mb-1 block text-xs font-semibold text-muted-foreground">Rotary Year</label>
          <select data-testid="add-admin-year" className={fieldClass} value={ry} onChange={(e) => setRy(e.target.value)} disabled={!districtId}>
            <option value="">{districtId ? "Select…" : "Pick a district first"}</option>
            {years.map((y) => <option key={y.rotary_year_id} value={y.rotary_year_id}>{y.label}</option>)}
          </select>
        </div>
        <div className="lg:col-span-1">
          <label className="mb-1 block text-xs font-semibold text-muted-foreground">Email</label>
          <Input data-testid="add-admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.org" />
        </div>
        <div className="lg:col-span-1">
          <label className="mb-1 block text-xs font-semibold text-muted-foreground">Name <span className="font-normal">(if new)</span></label>
          <Input data-testid="add-admin-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
        </div>
        <div className="lg:col-span-1">
          <Button className="w-full" onClick={submit} disabled={busy} data-testid="add-admin-submit">
            <EnvelopeSimple size={15} weight="bold" className="mr-1.5" /> {busy ? "Sending…" : "Send invite"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function StatusPill({ status }) {
  const active = status !== "inactive" && status !== "disabled";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
      active ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export default function Support() {
  const navigate = useNavigate();
  const [principal, setPrincipal] = useState(null);
  const [tab, setTab] = useState("districts");
  const [districts, setDistricts] = useState([]);
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [dialog, setDialog] = useState(null);

  const loadSuperAdmins = useCallback(async () => {
    try { setSuperAdmins((await api.get("/support/super-admins")).data.super_admins || []); } catch {}
  }, []);

  const loadDistricts = useCallback(async () => {
    try {
      const { data } = await api.get("/support/districts");
      setDistricts(data.districts || []);
    } catch { /* gated below */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/principal");
        setPrincipal(data);
      } catch { setPrincipal({}); }
    })();
    loadDistricts();
  }, [loadDistricts]);

  useEffect(() => { if (tab === "supers") loadSuperAdmins(); }, [tab, loadSuperAdmins]);

  const addSuperAdmin = () => setDialog({
    title: "Add super admin", submitLabel: "Send activation email",
    successMsg: "Super admin added — activation email sent",
    fields: [
      { name: "email", label: "Email", placeholder: "person@example.org",
        hint: "They get owner access and an activation email to set up their own login." },
      { name: "display_name", label: "Full name", placeholder: "e.g. Jane Doe" },
    ],
    action: async (v) => { await api.post("/support/super-admins", v); await loadSuperAdmins(); },
  });

  const searchUsers = async () => {
    try {
      const { data } = await api.get(`/support/users?q=${encodeURIComponent(q)}`);
      setUsers(data.users || []);
    } catch { toast.error("Search failed"); }
  };

  if (principal && principal.is_platform_owner === false) {
    return (
      <div className="mx-auto max-w-lg py-20 text-center" data-testid="support-not-authorized">
        <Lifebuoy size={40} weight="duotone" className="mx-auto mb-3 text-muted-foreground" />
        <h2 className="text-lg font-bold">Support Console</h2>
        <p className="mt-1 text-sm text-muted-foreground">You do not have Platform Owner access.</p>
      </div>
    );
  }

  // ---- district actions ----
  const openContext = (d) => setDialog({
    title: `Open District ${d.rotary_district_no} in support`,
    submitLabel: "Open support context", successMsg: "Support context opened",
    fields: [{ name: "rotary_year_id", label: "Rotary Year", type: "select",
      options: (d.rotary_years || []).map((r) => ({ value: r.rotary_year_id, label: r.label })) }],
    action: async (v) => {
      await api.post("/support/context", { district_id: d.district_id, rotary_year_id: v.rotary_year_id, reason: v.reason });
      navigate("/");
    },
  });

  const editDistrict = (d) => setDialog({
    title: `Edit District ${d.rotary_district_no}`, submitLabel: "Save",
    fields: [
      { name: "name", label: "Name", default: d.name },
      { name: "timezone", label: "Timezone", default: d.timezone },
    ],
    action: (v) => api.patch(`/support/districts/${d.district_id}`,
      { reason: v.reason, name: v.name, timezone: v.timezone }),
  });

  const toggleDistrict = (d) => {
    const inactive = d.status === "inactive";
    setDialog({
      title: `${inactive ? "Activate" : "Deactivate"} District ${d.rotary_district_no}`,
      submitLabel: inactive ? "Activate" : "Deactivate",
      action: (v) => api.post(`/support/districts/${d.district_id}/${inactive ? "activate" : "deactivate"}`, { reason: v.reason }),
    });
  };

  const assignAdmin = (d) => setDialog({
    title: `Assign District Admin · District ${d.rotary_district_no}`, submitLabel: "Assign",
    fields: [
      { name: "rotary_year_id", label: "Rotary Year", type: "select",
        options: (d.rotary_years || []).map((r) => ({ value: r.rotary_year_id, label: r.label })) },
      { name: "email", label: "Person's email", placeholder: "person@example.org",
        hint: "A new person gets an activation email to set up their own login." },
      { name: "display_name", label: "Full name (only if new person)", placeholder: "e.g. Jane Doe" },
    ],
    successMsg: "District admin assigned — activation email sent",
    action: (v) => api.post(`/support/districts/${d.district_id}/admin`,
      { reason: v.reason, email: v.email, display_name: v.display_name,
        rotary_year_id: v.rotary_year_id, send_invite: true }),
  });

  const addRy = (d) => setDialog({
    title: `Add Rotary Year · District ${d.rotary_district_no}`, submitLabel: "Add",
    fields: [
      { name: "label", label: "Label (e.g. 2027-28)", placeholder: "2027-28" },
      { name: "activate", label: "Activate now?", type: "select",
        options: [{ value: "", label: "No" }, { value: "yes", label: "Yes" }] },
    ],
    action: (v) => api.post(`/support/districts/${d.district_id}/rotary-years`,
      { reason: v.reason, label: v.label, activate: v.activate === "yes" }),
  });

  const createDistrict = () => setDialog({
    title: "Create District", submitLabel: "Create",
    fields: [
      { name: "rotary_district_no", label: "District Number", placeholder: "3000" },
      { name: "name", label: "Name", placeholder: "Rotary District 3000" },
      { name: "country", label: "Country", placeholder: "India" },
      { name: "timezone", label: "Timezone", default: "Asia/Kolkata" },
    ],
    action: (v) => api.post("/support/districts", v),
  });

  // ---- user actions ----
  const viewAs = (u) => setDialog({
    title: `View as ${u.display_name || u.email}`, submitLabel: "Start read-only view",
    successMsg: "Read-only view started",
    action: async (v) => {
      await api.post("/support/view-as", { target_user_id: u.user_id, reason: v.reason });
      navigate("/");
    },
  });
  const toggleUser = (u) => {
    const disabled = u.operational_access_revoked;
    setDialog({
      title: `${disabled ? "Enable" : "Disable"} ${u.display_name || u.email}`,
      submitLabel: disabled ? "Enable" : "Disable",
      action: async (v) => { await api.post(`/support/users/${u.user_id}/${disabled ? "enable" : "disable"}`, { reason: v.reason }); await searchUsers(); },
    });
  };
  const unlockUser = (u) => setDialog({
    title: `Unlock & revoke sessions · ${u.display_name || u.email}`, submitLabel: "Unlock",
    action: (v) => api.post(`/support/users/${u.user_id}/unlock`, { reason: v.reason }),
  });
  const inviteUser = (u) => setDialog({
    title: `Send invitation · ${u.display_name || u.email}`, submitLabel: "Send",
    action: (v) => api.post(`/support/users/${u.user_id}/invite`, { reason: v.reason }),
  });

  return (
    <div className="mx-auto max-w-5xl" data-testid="support-console">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#7C4DFF]">
          <Lifebuoy size={22} weight="fill" className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-extrabold tracking-tight">Platform Support Console</h1>
          <p className="text-sm text-muted-foreground">District, account and GOV support with full audit.</p>
        </div>
      </div>

      <div className="mb-5 flex gap-2 border-b border-border">
        {[["districts", "Districts", Buildings], ["users", "Users", UserGear], ["supers", "Super Admins", Lifebuoy]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`support-tab-${id}`} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon size={17} weight="duotone" /> {label}
          </button>
        ))}
      </div>

      {tab === "districts" && (
        <div className="space-y-4">
          <AddAdminCard districts={districts} onDone={loadDistricts} />
          <div className="flex justify-end">
            <Button size="sm" onClick={createDistrict} data-testid="support-create-district">
              <PlusCircle size={16} weight="bold" className="mr-1.5" /> Create District
            </Button>
          </div>
          {districts.map((d) => (
            <div key={d.district_id} data-testid={`support-district-${d.district_id}`}
              className="rounded-lg border border-border bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold">District {d.rotary_district_no}</p>
                    <StatusPill status={d.status} />
                  </div>
                  <p className="text-sm text-muted-foreground">{d.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Admin: {d.district_admin?.display_name || "—"} · {d.clubs_count} clubs ·
                    RY: {(d.rotary_years || []).map((r) => r.label).join(", ") || "—"}
                  </p>
                </div>
                <Button size="sm" onClick={() => openContext(d)} data-testid={`support-open-${d.district_id}`}>
                  Open in support
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => assignAdmin(d)} data-testid={`support-assign-admin-${d.district_id}`}>
                  <UserGear size={15} className="mr-1" /> Assign Admin
                </Button>
                <Button size="sm" variant="outline" onClick={() => addRy(d)} data-testid={`support-add-ry-${d.district_id}`}>
                  <CalendarPlus size={15} className="mr-1" /> Add Rotary Year
                </Button>
                <Button size="sm" variant="outline" onClick={() => editDistrict(d)} data-testid={`support-edit-${d.district_id}`}>
                  <PencilSimple size={15} className="mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleDistrict(d)} data-testid={`support-toggle-status-${d.district_id}`}>
                  <Power size={15} className="mr-1" /> {d.status === "inactive" ? "Activate" : "Deactivate"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div>
          <div className="mb-4 flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email"
              onKeyDown={(e) => e.key === "Enter" && searchUsers()} data-testid="support-user-search-input" />
            <Button onClick={searchUsers} data-testid="support-user-search-button">
              <MagnifyingGlass size={16} className="mr-1.5" /> Search
            </Button>
          </div>
          <div className="space-y-3">
            {users.map((u) => (
              <div key={u.user_id} data-testid={`support-user-${u.user_id}`}
                className="rounded-lg border border-border bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold">{u.display_name || u.email}</p>
                    <p className="text-xs text-muted-foreground">{u.email} · {u.user_id}</p>
                  </div>
                  <StatusPill status={u.operational_access_revoked ? "disabled" : "active"} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => viewAs(u)} data-testid={`support-view-as-${u.user_id}`}>
                    <Eye size={15} className="mr-1" /> View as
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => inviteUser(u)} data-testid={`support-invite-${u.user_id}`}>
                    <EnvelopeSimple size={15} className="mr-1" /> Invite
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => unlockUser(u)} data-testid={`support-unlock-${u.user_id}`}>
                    <LockKeyOpen size={15} className="mr-1" /> Unlock
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleUser(u)} data-testid={`support-toggle-user-${u.user_id}`}>
                    <Power size={15} className="mr-1" /> {u.operational_access_revoked ? "Enable" : "Disable"}
                  </Button>
                </div>
              </div>
            ))}
            {users.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">Search to find user accounts.</p>
            )}
          </div>
        </div>
      )}

      {tab === "supers" && (
        <div className="space-y-4" data-testid="support-supers">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Platform owners / super admins. New admins activate via email.</p>
            <Button size="sm" onClick={addSuperAdmin} data-testid="support-add-super-admin">
              <PlusCircle size={16} weight="bold" className="mr-1.5" /> Add super admin
            </Button>
          </div>
          {superAdmins.map((s) => (
            <div key={s.user_id} data-testid={`support-super-${s.user_id}`}
              className="flex items-center justify-between rounded-lg border border-border bg-white p-4">
              <div>
                <p className="font-bold">{s.display_name || s.email}</p>
                <p className="text-xs text-muted-foreground">{s.email}</p>
              </div>
              <StatusPill status={s.account_status === "active" ? "active" : "inactive"} />
            </div>
          ))}
          {superAdmins.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">No additional super admins yet.</p>
          )}
        </div>
      )}

      {dialog && (
        <FormDialog spec={dialog} onClose={(changed) => { setDialog(null); if (changed) loadDistricts(); }} />
      )}
    </div>
  );
}
